// CleverTap Utility Functions
// Centralized utility for CleverTap user tracking and event management

import {
  loadCleverTapSDK,
  isCleverTapReady,
  initializeCleverTapConfig,
  isCleverTapEnabled,
  CLEVERTAP_CONFIG,
} from './clevertapConfig';
import store from '../store';

/**
 * Formats user data for CleverTap according to their expected format
 * @param {Object} user - User object from your API
 * @returns {Object} - Formatted user data for CleverTap
 */
export const formatUserForCleverTap = (user) => {
  if (!user) return null;

  // Handle phone number formatting safely
  const formatPhoneNumber = (phoneNo) => {
    if (!phoneNo) return undefined;
    // If phone number already has country code, use as is
    if (phoneNo.startsWith('+')) return phoneNo;
    // Add +91 prefix if not present
    return `+91${phoneNo}`;
  };

  const userProfile = {
    Site: {
      Identity: String(user?.user_id || user?.id),
      Name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || undefined,
      Email: user?.email,
      Phone: formatPhoneNumber(user?.phone_no),
      'First Name': user?.first_name,
      'Last Name': user?.last_name,
      'Phone Number': formatPhoneNumber(user?.phone_no),
      'User ID': String(user?.user_id || user?.id),
      'Platform': 'web',
      'Login Time': new Date().toISOString(),
      'User Type': user?.company_details?.company_type || undefined,
      'Is Verified': user?.is_verified || false,
      'Is Firm Onboarded': user?.is_firm_onboarded || user?.is_onboarded || false,
      'Access Type': user?.access_type || 'unknown',
      'PWA Module': user?.pwa_module || false,
      'MSG-whatsapp': true, // Assuming WhatsApp is always enabled
    },
  };

  // Remove undefined values
  Object.keys(userProfile.Site).forEach((key) => {
    if (userProfile.Site[key] === undefined) {
      delete userProfile.Site[key];
    }
  });

  return userProfile;
};

/**
 * Initialize CleverTap SDK and track user login
 * This is the main function to call when a user logs in
 * @param {Object} user - User object from your API
 * @returns {Promise} - Resolves when user data is tracked
 */
export const initializeCleverTapAndTrackUser = async (user) => {
  // Skip if CleverTap is disabled (non-production environment)
  if (!isCleverTapEnabled()) {
    console.log('CleverTap tracking disabled - not enabled for this environment');
    return;
  }

  if (!user) {
    return;
  }

  try {
    // Ensure CleverTap is properly configured
    ensureCleverTapConfig();

    // Load CleverTap SDK first
    await loadCleverTapSDK();

    // Double-check configuration after SDK loads
    ensureCleverTapConfig();

    // Track the user login
    await trackUserLogin(user);
  } catch (error) {
    // Silent fail
  }
};

export const initializeCleverTapAction = async (userProfileData) => {
  // Initialize CleverTap and track user for both PWA and regular users
  try {
    await initializeCleverTapAndTrackUser(userProfileData);
  } catch (error) {
    // Don't block the app if CleverTap fails
  }
};

/**
 * Safely push user login data to CleverTap
 * @param {Object} user - User object from your API
 * @returns {Promise} - Resolves when user data is pushed
 */
export const trackUserLogin = (user) => {
  return new Promise((resolve) => {
    if (!user) {
      resolve();
      return;
    }

    const userProfile = formatUserForCleverTap(user);
    let resolved = false;

    const pushUserData = () => {
      try {
        // Validate CleverTap is properly initialized
        if (!window.clevertap) {
          return;
        }

        // Check if account is configured
        if (
          !window.clevertap.account ||
          window.clevertap.account.length === 0
        ) {
          // Try to reinitialize configuration
          initializeCleverTapConfig();
        }

        // Push user login data (for login tracking)
        if (window.clevertap.onUserLogin) {
          window.clevertap.onUserLogin.push(userProfile);
        }

        // Push profile data (for user attributes)
        if (window.clevertap.profile) {
          window.clevertap.profile.push(userProfile);
        }

        // Track login event using standardized format with explicit user data
        trackAnalyticsEventWithUserData('USER_LOGIN', {
          platform: 'web',
          login_time: new Date().toISOString(),
          login_method: 'web_login',
          is_benefit_enabled: user.is_benefit_enabled || false,
        }, user);

        // Force flush to send data immediately
        if (window.clevertap.flush) {
          window.clevertap.flush();
        }
      } catch (error) {
        // Silent fail
      }

      if (!resolved) {
        resolved = true;
        resolve();
      }
    };

    // Check if CleverTap is ready
    if (isCleverTapReady()) {
      pushUserData();
    } else {
      waitForCleverTap(pushUserData, () => {
        if (!resolved) {
          resolved = true;
          resolve();
        }
      });
    }
  });
};

/**
 * Wait for CleverTap SDK to be ready
 * @param {Function} callback - Function to call when ready
 * @param {Function} fallback - Function to call if timeout
 */
export const waitForCleverTap = (callback, fallback) => {
  let resolved = false;

  // Ensure clevertap object exists
  window.clevertap = window.clevertap || {};
  window.clevertap.notifications = window.clevertap.notifications || [];

  // Listen for CleverTap ready event
  try {
    window.clevertap.notifications.push({
      event: 'clevertapReady',
      callback: function () {
        if (!resolved && callback) {
          resolved = true;
          callback();
        }
      },
    });
  } catch (error) {
    // Silent fail for ready event listener setup
  }

  // Polling fallback
  let attempts = 0;
  const maxAttempts = 15; // 4.5 seconds max wait
  const pollInterval = 300;

  const pollForReady = () => {
    attempts++;

    if (isCleverTapReady()) {
      if (!resolved && callback) {
        resolved = true;
        callback();
      }
    } else if (attempts < maxAttempts) {
      setTimeout(pollForReady, pollInterval);
    } else {
      if (!resolved && fallback) {
        resolved = true;
        fallback();
      }
    }
  };

  // Start polling after a short delay
  setTimeout(pollForReady, 100);

  // Final timeout fallback
  setTimeout(() => {
    if (!resolved) {
      resolved = true;
      if (fallback) fallback();
    }
  }, 6000);
};

/**
 * Track a custom event to CleverTap
 * @param {string} eventName - Name of the event
 * @param {Object} eventData - Event properties
 * @deprecated Use trackAnalyticsEvent for new implementations
 */
export const trackEvent = (eventName, eventData = {}) => {
  // Use the new generic function for consistency
  return trackAnalyticsEvent(eventName, eventData);
};

/**
 * Update user profile data
 * @param {Object} profileData - Profile data to update
 */
export const updateUserProfile = (profileData) => {
  if (!isCleverTapReady()) {
    return;
  }

  try {
    const formattedProfile = {
      Site: {
        ...profileData,
        'Last Updated': new Date().toISOString(),
      },
    };

    window.clevertap.profile.push(formattedProfile);

    if (window.clevertap.flush) {
      window.clevertap.flush();
    }
  } catch (error) {
    // Silent fail
  }
};

/**
 * Track user logout event
 */
export const trackUserLogout = (logoutMethod = 'web_logout') => {
  // Always perform cleanup, but track event only if CleverTap is ready
  let eventTracked = false;
  
  try {
    // Try to track logout event if CleverTap is ready
    if (window.clevertap && isCleverTapReady()) {
      trackAnalyticsEvent('USER_LOGOUT', {
        logout_time: new Date().toISOString(),
        logout_method: logoutMethod,
        platform: 'web',
      });
      eventTracked = true;
    }

    // Always perform session cleanup regardless of CleverTap state
    // This ensures PWA fresh starts work even if CleverTap isn't fully loaded
    performCleverTapCleanup();
    
  } catch (error) {
    // Silent fail but still try cleanup
    try {
      performCleverTapCleanup();
    } catch (e) {
      // Ultimate silent fail
    }
  }
  
  return eventTracked;
};

/**
 * Perform comprehensive CleverTap session cleanup
 * Works regardless of CleverTap ready state
 */
const performCleverTapCleanup = () => {
  try {
    // Ensure clevertap object exists
    window.clevertap = window.clevertap || {};

    // Try to logout if the function exists
    if (window.clevertap.logout) {
      window.clevertap.logout();
    }

    // Always clear cookies regardless of CleverTap state
    clearCleverTapCookies();

    // Clear any session storage that might persist user state
    try {
      sessionStorage.removeItem('clevertap_page_view_count');
    } catch (e) {
      // Ignore if sessionStorage is not available
    }

    // Reset CleverTap arrays to ensure clean state
    window.clevertap.event = window.clevertap.event || [];
    window.clevertap.profile = window.clevertap.profile || [];
    window.clevertap.onUserLogin = window.clevertap.onUserLogin || [];

  } catch (error) {
    // Silent fail - but at least try to clear cookies
    try {
      clearCleverTapCookies();
    } catch (e) {
      // Ultimate silent fail
    }
  }
};

const clearCleverTapCookies = () => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
    document.cookie =
      name.trim() + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
  }
};

/**
 * Ensure CleverTap is properly configured and ready
 * This function validates and fixes common configuration issues
 */
export const ensureCleverTapConfig = () => {
  try {
    // Ensure basic structure exists
    window.clevertap = window.clevertap || {
      event: [],
      profile: [],
      account: [],
      onUserLogin: [],
      notifications: [],
      privacy: [],
    };

    // Check if account is configured (should be from HTML)
    const hasValidAccount =
      window.clevertap.account &&
      window.clevertap.account.length > 0 &&
      window.clevertap.account.some((config) => config && config.id);

    if (!hasValidAccount) {
      initializeCleverTapConfig();
    }

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get device and viewport properties for analytics
 * @returns {Object} Device and viewport properties
 */
const getDeviceAndViewportProperties = () => {
  try {
    return {
      // Screen resolution
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      
      // Viewport size
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      
      // Device pixel ratio for high-DPI displays
      device_pixel_ratio: window.devicePixelRatio || 1
    };
  } catch (error) {
    return {};
  }
};

/**
 * Get or increment page view count for the session
 * @returns {number} Current page view count
 */
const getPageViewCount = () => {
  try {
    const sessionKey = 'clevertap_page_view_count';
    let count = parseInt(sessionStorage.getItem(sessionKey) || '0');
    count += 1;
    sessionStorage.setItem(sessionKey, count.toString());
    return count;
  } catch (error) {
    return 1;
  }
};

/**
 * Generic analytics event tracking function
 * Works across all modules and accepts any properties
 * @param {string} eventName - The event name
 * @param {Object} eventProperties - Event-specific properties
 */
export const trackAnalyticsEvent = (eventName, eventProperties = {}) => {
  try {
    // Skip if CleverTap is disabled (non-production environment)
    if (!isCleverTapEnabled()) {
      console.log(`[CleverTap] Tracking disabled - not enabled for this environment for event: ${eventName}`);
      return;
    }

    if (!window.clevertap || !window.clevertap.event) {
      return;
    }

    // Get current state from Redux store
    const state = store.store.getState();
    const userData = state?.user?.userData || state?.loginReducer?.userData;
    const is_PWA = state?.loginReducer?.is_PWA || false;

    // Get device and viewport properties
    const deviceProperties = getDeviceAndViewportProperties();

    // Get page view count
    const pageViewCount = getPageViewCount();

    // Build base properties that are common across all events
    const baseProperties = {
      platform: 'web',
      timestamp: new Date().toISOString(),
      is_PWA: is_PWA,
      user_id: userData?.user_id || userData?.id,
      manufacturer_id: userData?.company_details?.company_id,
      company_type: userData?.company_details?.company_type,
      
      // Page analytics
      page_view_count: pageViewCount,
      session_page_count: pageViewCount, // Alias for clarity
      
      // Device and viewport properties
      ...deviceProperties,
      
      // Event-specific properties (will override base properties if same keys)
      ...eventProperties
    };

    // Remove undefined values to keep the payload clean
    Object.keys(baseProperties).forEach(key => {
      if (baseProperties[key] === undefined) {
        delete baseProperties[key];
      }
    });

    // Track the event
    window.clevertap.event.push(eventName, baseProperties);

    // Force flush to send immediately
    if (window.clevertap.flush) {
      window.clevertap.flush();
    }
  } catch (error) {
    // Silent fail in production
  }
};

/**
 * Track analytics event with explicit user data (for login scenarios)
 * @param {string} eventName - The name of the event to track
 * @param {Object} eventProperties - Event-specific properties
 * @param {Object} userData - User data to use instead of Redux store
 */
export const trackAnalyticsEventWithUserData = (eventName, eventProperties = {}, userData = null) => {
  try {
    // Skip if CleverTap is disabled (non-production environment)
    if (!isCleverTapEnabled()) {
      console.log(`[CleverTap] Tracking disabled - not enabled for this environment for event: ${eventName}`);
      return;
    }

    if (!window.clevertap || !window.clevertap.event) {
      return;
    }

    // Get current state from Redux store for PWA flag
    const state = store.store.getState();
    const is_PWA = state?.loginReducer?.is_PWA || false;

    // Use provided userData or fallback to Redux store
    const user = userData || state?.user?.userData || state?.loginReducer?.userData;

    // Get device and viewport properties
    const deviceProperties = getDeviceAndViewportProperties();

    // Get page view count
    const pageViewCount = getPageViewCount();

    // Build base properties that are common across all events
    const baseProperties = {
      platform: 'web',
      timestamp: new Date().toISOString(),
      is_PWA: is_PWA,
      user_id: user?.user_id || user?.id,
      manufacturer_id: user?.company_details?.company_id,
      company_type: user?.company_details?.company_type,
      
      // Page analytics
      page_view_count: pageViewCount,
      session_page_count: pageViewCount,
      
      // Device and viewport properties
      ...deviceProperties,
      
      // Event-specific properties (will override base properties if same keys)
      ...eventProperties
    };

    // Remove undefined values to keep the payload clean
    Object.keys(baseProperties).forEach(key => {
      if (baseProperties[key] === undefined) {
        delete baseProperties[key];
      }
    });

    // Track the event
    window.clevertap.event.push(eventName, baseProperties);

    // Force flush to send immediately
    if (window.clevertap.flush) {
      window.clevertap.flush();
    }
  } catch (error) {
    // Silent fail in production
  }
};

export const trackPWAModuleDetection = (userProfileData, detectionSource = 'token_data', delay = 1500) => {
  if (!userProfileData) {
    console.warn('[CleverTap] Cannot track PWA detection - no user profile data provided');
    return;
  }

  const trackPWADetection = () => {
    trackAnalyticsEventWithUserData('PWA_MODULE_DETECTED', {
      user_id: userProfileData.user_id || userProfileData.id,
      pwa_module: true,
      detection_source: detectionSource,
      timestamp: new Date().toISOString()
    }, userProfileData);
  };

  if (delay > 0) {
    // Use setTimeout to ensure CleverTap profile is processed before tracking the event
    setTimeout(trackPWADetection, delay);
  } else {
    // Immediate tracking (may result in anonymous events if profile not processed)
    trackPWADetection();
  }
};

// Export the main initialization function for easy import
export { initializeCleverTapAndTrackUser as default };

/**
 * Get is_PWA flag from Redux store
 * @returns {boolean} - Whether the user is accessing via PWA
 */
export const getIsPWAFromStore = () => {
  try {
    const state = store.store.getState();
    return state?.loginReducer?.is_PWA || false;
  } catch (error) {
    return false;
  }
};
